import {useContext, useState, useEffect} from "react";
import {Modal} from "../../../Smart/Modal/Modal";
import {Button} from "../../../Form/Button/Button";
import {I18nReactContext} from "../../../../contexts/I18nContext";
import {buttonColors} from "../../../Pickers/InputTheme";
import {CourseTemplateBank} from "../../../WeezBeeCourse/components/ContentPane/ChapterEditor/CourseTemplateBank";
import {AIContextGenerator} from "../AIContextGenerator/AIContextGenerator";
import {generateCourseWithTemplateAPI} from "../../../../Services/api";
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import './AICourseGeneratorWizard.scss';

/**
 * AICourseGeneratorWizard Component
 *
 * A multi-step wizard modal for AI course generation.
 * Step 1: Collect course details (subject, instructions, file upload)
 * Step 2: Select a course template
 *
 * @component
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the wizard modal is open
 * @param {Function} props.onClose - Callback when wizard is closed
 * @param {string} props.signDocId - The SignDoc ID for the course
 * @param {Function} props.onGenerationStart - Callback when AI generation starts
 * @returns {JSX.Element} The rendered wizard modal
 */
export const AICourseGeneratorWizard = ({
    open,
    onClose,
    signDocId,
    onGenerationStart
}) => {
    const {i18n} = useContext(I18nReactContext);

    // Wizard state
    const [activeStep, setActiveStep] = useState(0);
    const [selectedTemplate, setSelectedTemplate] = useState(null);

    // Context data from AIContextGenerator
    const [contextData, setContextData] = useState(null);
    const [isContextValid, setIsContextValid] = useState(false);

    // Generation state
    const [isGenerating, setIsGenerating] = useState(false);

    // Stepper labels
    const steps = [
        i18n.translate('weezbeeCourse.aiWizard.step1Label'),
        i18n.translate('weezbeeCourse.aiWizard.step2Label')
    ];

    // Reset state when modal closes
    useEffect(() => {
        if (!open) {
            setActiveStep(0);
            setSelectedTemplate(null);
            setContextData(null);
            setIsContextValid(false);
            setIsGenerating(false);
        }
    }, [open]);

    /**
     * Handles context data changes from AIContextGenerator
     * @param {Object} data - The context data {subject, promptInstructions, extractedFileText, uploadedFiles}
     */
    const handleContextDataChange = (data) => {
        setContextData(data);
    };

    /**
     * Handles validation changes from AIContextGenerator
     * @param {Object} validation - The validation state {isValid, errors}
     */
    const handleContextValidationChange = ({isValid}) => {
        setIsContextValid(isValid);
    };

    /**
     * Validates Step 1 form and proceeds to Step 2
     */
    const handleNext = () => {
        if (isContextValid) {
            setActiveStep(1);
        }
    };

    /**
     * Goes back to previous step
     */
    const handleBack = () => {
        setActiveStep((prevStep) => prevStep - 1);
    };


    /**
     * Handles AI generation when Generate button is clicked
     * Collects all wizard data and triggers AI generation via API
     * @returns {Promise<void>}
     */
    const handleGenerate = async () => {
        // Validate required fields
        if (!contextData || !contextData.subject.trim()) {
            console.error('No context data or subject');
            return;
        }

        if (!selectedTemplate) {
            console.error('No template selected');
            return;
        }

        // Set loading state
        setIsGenerating(true);

        try {
            // Prepare API parameters
            const apiParams = {
                signDocId,
                description: contextData.subject,
                courseTemplate: selectedTemplate
            };

            // Add optional custom prompt if provided
            if (contextData.promptInstructions && contextData.promptInstructions.trim()) {
                apiParams.customPrompt = contextData.promptInstructions;
            }

            // Add optional file content if provided (exclude PDF placeholder text and errors)
            if (contextData.extractedFileText &&
                !contextData.extractedFileText.startsWith('[PDF file:') &&
                !contextData.extractedFileText.startsWith('[Error:')) {
                apiParams.fileContent = contextData.extractedFileText;
            }

            // Add file name if file was uploaded
            if (contextData.uploadedFiles && contextData.uploadedFiles.length > 0) {
                apiParams.fileName = contextData.uploadedFiles[0].name;
            }

            // Call the API to start generation
            await generateCourseWithTemplateAPI(apiParams);

            // Notify parent component that generation has started
            if (onGenerationStart) {
                onGenerationStart({ signDocId });
            }

            // Close the wizard modal
            onClose();
        } catch (error) {
            console.error('Error starting AI generation:', error);
            setIsGenerating(false);
        }
    };

    return (
        <Modal open={open} onClose={onClose}>
            <div className="AICourseGeneratorWizard">
                {/* Wizard Title */}
                <div className="AICourseGeneratorWizard__header">
                    <h2 className="AICourseGeneratorWizard__title">
                        {i18n.translate('weezbeeCourse.aiWizard.wizardTitle')}
                    </h2>
                </div>

                {/* Stepper */}
                <div className="AICourseGeneratorWizard__stepper">
                    <Stepper activeStep={activeStep} alternativeLabel>
                        {steps.map((label) => (
                            <Step key={label}>
                                <StepLabel>{label}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                </div>

                {/* Content */}
                <div className="AICourseGeneratorWizard__content">
                    {/* Step 1: Course Details - Using AIContextGenerator */}
                    {activeStep === 0 && (
                        <div className="AICourseGeneratorWizard__step1">
                            <AIContextGenerator
                                embeddedMode={true}
                                onDataChange={handleContextDataChange}
                                onValidationChange={handleContextValidationChange}
                            />
                        </div>
                    )}

                    {/* Step 2: Template Selection */}
                    {activeStep === 1 && (
                        <div className="AICourseGeneratorWizard__step2">
                            <CourseTemplateBank
                                embeddedMode={true}
                                description={contextData?.subject || ''}
                                signDocId={signDocId}
                                onTemplateSelect={(template) => setSelectedTemplate(template)}
                                onClose={handleBack}
                                onSelectCourseTemplate={() => {}}
                            />
                        </div>
                    )}
                </div>

                {/* Footer Navigation */}
                <div className="AICourseGeneratorWizard__footer">
                    <Button
                        text={i18n.translate('general.cancel')}
                        onClick={onClose}
                        color={buttonColors.GREY}
                        variant="outlined"
                        dataTestId="wizard-cancel-button"
                    />

                    <div className="AICourseGeneratorWizard__footer-right">
                        {activeStep > 0 && (
                            <Button
                                text={i18n.translate('general.back')}
                                onClick={handleBack}
                                color={buttonColors.GREY}
                                variant="outlined"
                                dataTestId="wizard-back-button"
                            />
                        )}

                        {activeStep === 0 && (
                            <Button
                                text={i18n.translate('general.next')}
                                onClick={handleNext}
                                disabled={!isContextValid}
                                color={buttonColors.YELLOW}
                                variant="contained"
                                dataTestId="wizard-next-button"
                            />
                        )}

                        {activeStep === 1 && (
                            <Button
                                text={i18n.translate('weezbeeCourse.aiWizard.generateButton')}
                                onClick={handleGenerate}
                                disabled={!selectedTemplate || isGenerating}
                                color={buttonColors.YELLOW}
                                variant="contained"
                                dataTestId="wizard-generate-button"
                            />
                        )}
                    </div>
                </div>
            </div>
        </Modal>
    );
};
